import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { trackEvent, EventCategory } from '../../lib/analytics'
import { Loader2, Users, Brain, FileBarChart } from 'lucide-react'
import { safePropertyAccess } from '../../lib/type-fixes'

type AuthMethod = 'pin' | null
type ViewState = 'auth' | 'results'

interface SurveyCode {
  code: string
  organization_name: string | null
  total_responses: number
  created_at: string
}

export function SurveyResults() {
  const [authMethod, setAuthMethod] = useState<AuthMethod>(null)
  const [viewState, setViewState] = useState<ViewState>('auth')
  const [code, setCode] = useState('')
  const [managerPin, setManagerPin] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [surveyData, setSurveyData] = useState<{
    totalResponses: number;
    organizationName: string | null;
    roleResponses: Array<{
      role_name: string;
      role_function: string;
      role_level: string;
      custom_role_name: string | null;
      response_count: number;
    }>;
  } | null>(null)

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Get survey responses grouped by role
      const { data: responses, error: responsesError } = await supabase
        .from('survey_responses')
        .select(`
          role:roles(name, function, level),
          custom_role_name
        `)
        .eq('survey_code', code.toUpperCase())
        .eq('status', 'completed')

      if (responsesError) throw responsesError

      // Process responses into role groups
      const roleGroups = responses.reduce((acc: Record<string, any>, curr) => {
        const key = safePropertyAccess(curr.role, 'id') || curr.custom_role_name || 'Unknown'
        
        if (!acc[key]) {
          acc[key] = {
            role_name: safePropertyAccess(curr.role, 'name') || 'Custom Role',
            role_function: safePropertyAccess(curr.role, 'function') || 'Other',
            role_level: safePropertyAccess(curr.role, 'level') || 'Other',
            custom_role_name: curr.custom_role_name,
            response_count: 0
          }
        }
        
        acc[key].response_count++
        return acc
      }, {})

      // Get survey details
      const { data: surveyDetails, error: surveyError } = await supabase
        .from('survey_codes')
        .select('organization_name')
        .eq('code', code.toUpperCase())
        .single()

      if (surveyError) throw surveyError

      setSurveyData({
        totalResponses: responses.length,
        organizationName: surveyDetails.organization_name,
        roleResponses: Object.values(roleGroups)
      })

      trackEvent({
        action: 'success',
        category: EventCategory.Survey,
        label: 'survey_results_loaded'
      })

      setViewState('results')
    } catch (error) {
      console.error('Error loading survey results:', error)
      trackEvent({
        action: 'error',
        category: EventCategory.Survey,
        label: 'survey_results_error'
      })
      toast.error('Invalid survey code or manager PIN.')
    } finally {
      setIsLoading(false)
    }
  }

  if (viewState === 'auth') {
    return (
      <div className="mt-16 max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Access Survey Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handlePinSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="code" className="text-sm font-medium">
                    Survey Code
                  </label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Enter 8-digit code"
                    maxLength={8}
                    className="text-center uppercase"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="pin" className="text-sm font-medium">
                    Manager PIN
                  </label>
                  <Input
                    id="pin"
                    type="password"
                    value={managerPin}
                    onChange={(e) => setManagerPin(e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder="Enter 6-digit PIN"
                    maxLength={6}
                    className="text-center"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || code.length !== 8 || managerPin.length !== 6}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'View Results'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="mt-16 max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        <Card>
          <CardHeader>
            <CardTitle>
              {surveyData?.organizationName || 'Survey'} Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-lg font-medium">Total Responses</p>
                <p className="text-3xl font-bold">{surveyData?.totalResponses}</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium">Response Distribution</h3>
              <div className="border rounded-lg divide-y">
                {surveyData?.roleResponses.map((response, index) => (
                  <div key={index} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">
                          {response.custom_role_name || response.role_name}
                        </h4>
                        {!response.custom_role_name && (
                          <p className="text-sm text-muted-foreground">
                            {response.role_function} • {response.role_level}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{response.response_count}</p>
                        <p className="text-sm text-muted-foreground">
                          {response.response_count === 1 ? 'Response' : 'Responses'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}